import {
	DocumentFormat,
	DocumentFormatValues,
	DocumentTemplate,
} from "@dune/types";
import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Label,
	RadioGroup,
	RadioGroupItem,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@dune/ui";
import { FileCode, FileText } from "lucide-react";
import React, { useMemo, useState, useId } from "react";
import { toast } from "sonner";
import { useGenerateQuittance } from "../../../../api/mutations/receipts.mutations";
import DuneSignatorySelect from "../../../../component/common/DuneSignatorySelect";
import { useGetDocumentTemplates } from "../../../../api/queries/documents.queries";
import { useReceiptsByPropositionQuery } from "../../../../api/queries/receipt.queries";

interface GenerateQuittancementModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	propositionId: number;
	onSuccess?: (data?: {
		documentUrl: string;
		documentId: string;
		fileId: string;
		message: string;
	}) => void;
}

const GenerateQuittancementModal: React.FC<GenerateQuittancementModalProps> = ({
	open,
	onOpenChange,
	propositionId,
	onSuccess,
}) => {
	const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
	const [selectedSignatoryId, setSelectedSignatoryId] = useState<string>("");
	const [selectedReceiptId, setSelectedReceiptId] = useState<string>("");
	const [selectedFormat, setSelectedFormat] = useState<DocumentFormatValues>(
		DocumentFormat.PDF,
	);
	const formatRadioId = useId();

	const { data: allTemplates, isLoading: isLoadingTemplates } =
		useGetDocumentTemplates();

	const { data: receipts, isLoading: isLoadingReceipts } =
		useReceiptsByPropositionQuery(propositionId);

	const validTemplates = useMemo(() => {
		return (allTemplates || []).filter(
			(template: DocumentTemplate) =>
				template.entityModel === "QUITTANCE" && template.validated,
		);
	}, [allTemplates]);

	const availableReceipts = useMemo(() => {
		return (
			receipts?.map((receipt) => ({
				id: receipt.id.toString(),
				label: receipt.reference,
				value: receipt.id.toString(),
				subtitle: receipt.type,
			})) || []
		);
	}, [receipts]);

	const generateQuittance = useGenerateQuittance(
		propositionId,
		selectedReceiptId,
	);

	const handleGenerate = async () => {
		if (!selectedTemplateId) {
			toast.error("Veuillez sélectionner un modèle de quittance");
			return;
		}

		if (!selectedSignatoryId) {
			toast.error("Veuillez sélectionner un signataire Dune");
			return;
		}

		generateQuittance.mutate(
			{
				templateId: selectedTemplateId,
				signataireDuneId: Number(selectedSignatoryId),
				format: selectedFormat,
			},
			{
				onSuccess: (data) => {
					toast.success("Quittance générée avec succès");
					onOpenChange(false);
					if (onSuccess) {
						onSuccess({
							...data,
							documentId: data.documentId.toString(),
							fileId: data.fileId.toString(),
						});
					}
				},
				onError: (error: unknown) => {
					const message =
						error instanceof Error
							? error.message
							: "Erreur lors de la génération";
					toast.error(message);
				},
			},
		);
	};

	const formatOptions = [
		{
			value: DocumentFormat.PDF,
			label: "PDF",
			description: "Pour signature électronique",
			icon: FileText,
		},
		{
			value: DocumentFormat.DOCX,
			label: "Word",
			description: "Pour permettre la modification",
			icon: FileCode,
		},
	];

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-lg">
				<DialogHeader>
					<DialogTitle>Générer une quittance</DialogTitle>
					<DialogDescription>
						Sélectionnez le modèle de quittance, le signataire Dune et le format
						de document
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					<div className="space-y-2">
						<label htmlFor="template-select" className="text-sm font-medium">
							Modèle de quittance
						</label>
						<Select
							value={selectedTemplateId}
							onValueChange={setSelectedTemplateId}
							disabled={isLoadingTemplates || validTemplates.length === 0}
						>
							<SelectTrigger id="template-select">
								<SelectValue
									placeholder={
										isLoadingTemplates
											? "Chargement..."
											: validTemplates.length === 0
												? "Aucun modèle disponible"
												: "Sélectionnez un modèle"
									}
								/>
							</SelectTrigger>
							<SelectContent>
								{validTemplates.map((template: DocumentTemplate) => (
									<SelectItem key={template.id} value={template.id}>
										{template.name} (v{template.version})
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className="space-y-2">
						<label htmlFor="receipt-select" className="text-sm font-medium">
							Quittance
						</label>
						<Select
							value={selectedReceiptId}
							onValueChange={setSelectedReceiptId}
							disabled={isLoadingReceipts || availableReceipts.length === 0}
						>
							<SelectTrigger id="receipt-select">
								<SelectValue
									placeholder={
										isLoadingReceipts
											? "Chargement..."
											: availableReceipts.length === 0
												? "Aucune quittance disponible"
												: "Sélectionnez une quittance"
									}
								/>
							</SelectTrigger>
							<SelectContent>
								{availableReceipts.map((receipt) => (
									<SelectItem key={receipt.id} value={receipt.id}>
										{receipt.label} ({receipt.subtitle})
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<DuneSignatorySelect
						value={selectedSignatoryId}
						onValueChange={setSelectedSignatoryId}
						id="generate-quittance-signatory-select"
						defaultToCurrentUser
					/>

					<fieldset className="space-y-2">
						<legend className="text-sm font-medium">Format du document</legend>
						<RadioGroup
							value={selectedFormat}
							onValueChange={(value) =>
								setSelectedFormat(value as DocumentFormatValues)
							}
							className="grid grid-cols-2 gap-3"
						>
							{formatOptions.map((option) => {
								const Icon = option.icon;
								return (
									<Label
										key={`${formatRadioId}-${option.value}`}
										htmlFor={`${formatRadioId}-${option.value}`}
										className="relative flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-input p-4 shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
									>
										<RadioGroupItem
											id={`${formatRadioId}-${option.value}`}
											value={option.value}
											className="absolute top-2 right-2"
										/>
										<Icon className="h-8 w-8 mb-1" />
										<span className="font-medium text-center">
											{option.label}
										</span>
										<span className="text-xs text-muted-foreground text-center">
											{option.description}
										</span>
									</Label>
								);
							})}
						</RadioGroup>
					</fieldset>

					{selectedTemplateId && selectedSignatoryId && (
						<div className="rounded-lg bg-blue-50 p-3">
							<p className="text-sm text-blue-800">
								La quittance sera générée au format{" "}
								{selectedFormat === DocumentFormat.PDF ? "PDF" : "Word"} et
								ajoutée automatiquement aux documents de la proposition.
							</p>
						</div>
					)}

					{validTemplates.length === 0 && !isLoadingTemplates && (
						<div className="rounded-lg bg-yellow-50 p-3">
							<p className="text-sm text-yellow-800">
								Aucun modèle de quittance validé n'est disponible. Veuillez
								d'abord créer et valider un modèle.
							</p>
						</div>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Annuler
					</Button>
					<Button
						onClick={handleGenerate}
						disabled={
							!selectedTemplateId ||
							!selectedSignatoryId ||
							generateQuittance.isPending ||
							validTemplates.length === 0
						}
						isLoading={generateQuittance.isPending}
					>
						Générer la quittance
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default GenerateQuittancementModal;
